import { useState } from 'react';
import { useApp } from '../store';
import { useSampleInert } from './SampleStrip';
import { canPlanInto, inOrder, type Sprint } from '../sprints';
import type { Issue } from '../types';

/**
 * PUTTING A CARD IN A SPRINT FROM THE CARD ITSELF.
 *
 * Vinnie, 15 August: *"If my board has a lot of cards opened, I should be able
 * to go to the card and click add to Sprint N. If there are multiple sprints
 * planned I should be able to select which sprint I want to add it into as well.
 * This gives the flexibility... either from the sprint screen or the board
 * screen."* And then: *"yes, add moving between sprints too."*
 *
 * TWO WAYS IN, ON PURPOSE. The sprint screen answers "what goes in this
 * fortnight" — you are looking at the sprint and choosing work. This answers
 * "where does this card go" — you are looking at the card, which is where you
 * already are when you have just read it and decided. Neither replaces the
 * other and the sprint screen is untouched.
 *
 * ONE CANDIDATE IS A BUTTON, SEVERAL ARE A CHOICE. With a single sprint to plan
 * into, a dropdown of one is a decision the software is pretending to need; the
 * button names the sprint so nothing is guessed. With several, it must ask —
 * silently picking "the running one" is exactly how work lands in the wrong
 * fortnight, and a fortnight is a commitment somebody made out loud.
 *
 * A CLOSED SPRINT IS NEVER OFFERED. Moving work into a finished fortnight
 * rewrites what that fortnight is said to have contained, months after its
 * report was read.
 */
export default function CardSprint({ issue }: { issue: Issue }) {
  const sprints = useApp((s) => s.sprints);
  const putInSprint = useApp((s) => s.putInSprint);
  const inert = useSampleInert();

  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');

  const open = inOrder(sprints).filter(canPlanInto);
  const now = issue.sprint ? sprints.find((s) => s.id === issue.sprint) : undefined;
  /* Where it could go INSTEAD — its own sprint is not a move. */
  const elsewhere = open.filter((s) => s.id !== issue.sprint);

  const move = async (to: string) => {
    setBusy(true);
    setProblem('');
    try {
      await putInSprint(issue.id, to);
      setPicking(false);
    } catch (e) {
      setProblem(
        (e as { message?: string })?.message || 'That could not be saved — nothing was changed.'
      );
    } finally {
      setBusy(false);
    }
  };

  /*
   * A CLOSED SPRINT STILL SHOWS, AND OFFERS NOTHING. The card belongs to a
   * fortnight that is over; saying so is the honest state, and quietly offering
   * to move it out of the record is not this screen's business.
   */
  if (issue.sprint && !now) {
    return <span className="card-sprint-was">In a finished sprint</span>;
  }
  if (open.length === 0 && !now) return null;

  return (
    <div className="card-sprint">
      {problem && (
        <span className="card-sprint-problem" role="alert">
          {problem}
        </span>
      )}

      {picking ? (
        <>
          <span className="card-sprint-ask">{now ? 'Move it to' : 'Add it to'}</span>
          {(now ? elsewhere : open).map((s) => (
            <button
              key={s.id}
              disabled={busy}
              {...inert('btn btn-small', () => void move(s.id))}
            >
              {label(s)}
            </button>
          ))}
          <button className="btn btn-small" disabled={busy} onClick={() => setPicking(false)}>
            Cancel
          </button>
        </>
      ) : now ? (
        <>
          <span className="epic-chip sprint-chip">{now.name}</span>
          {elsewhere.length > 0 && (
            <button disabled={busy} {...inert('btn btn-small', () => setPicking(true))}>
              {elsewhere.length === 1 ? `Move to ${elsewhere[0].name}` : 'Move to…'}
            </button>
          )}
          <button
            disabled={busy}
            {...inert('btn btn-small', () => void move(''), 'Back to the backlog — no work is deleted')}
          >
            Take out
          </button>
        </>
      ) : (
        <button
          disabled={busy}
          {...inert('btn btn-small', () => (open.length === 1 ? void move(open[0].id) : setPicking(true)))}
        >
          {open.length === 1 ? `Add to ${open[0].name}` : 'Add to a sprint…'}
        </button>
      )}
    </div>
  );
}

/** The running one says so, because "Sprint 5" alone does not say which is now. */
function label(s: Sprint): string {
  return s.state === 'active' ? `${s.name} (running)` : s.name;
}
